import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingCart, User, LogOut, LayoutDashboard, Boxes, Menu } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function Navbar() {
  const { user, logout, config } = useAuth();
  const { count } = useCart();
  const isOps = user?.role === "admin" || user?.role === "staff";
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 gap-4">
          <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="brand-logo">
            <img src="/logo.png" alt="Zealous Ventures" className="h-11 w-auto" />
            <div className="hidden sm:block h-8 w-px bg-slate-200" />
            <div className="hidden sm:block leading-tight">
              <div className="font-heading font-bold text-slate-900 text-sm tracking-tight">
                {config.business_name || "Zealous Ventures"}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400 font-medium">
                25+ Brands · B2B Wholesale
              </div>
            </div>
          </Link>

          {isOps && (
            <div className="hidden md:block" />
          )}

          <div className="flex items-center gap-2 sm:gap-3">
            {!isOps && (
            <Link to="/" className="hidden sm:block text-sm font-medium text-slate-600 hover:text-slate-900 px-3" data-testid="nav-catalogue">
              Catalogue
            </Link>
            )}

            {!isOps && (
            <Link to="/cart" className="relative" data-testid="nav-cart">
              <Button variant="outline" size="icon" className="rounded-full">
                <ShoppingCart className="h-5 w-5" />
              </Button>
              {count > 0 && (
                <span
                  data-testid="cart-count-badge"
                  className="absolute -top-1.5 -right-1.5 bg-[#FFDA00] text-slate-900 text-[11px] font-bold h-5 min-w-5 px-1 rounded-full flex items-center justify-center"
                >
                  {count}
                </span>
              )}
            </Link>
            )}

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" className="rounded-full" data-testid="user-menu-trigger">
                    <User className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="truncate">
                    {user.company_name || user.name}
                    <div className="text-xs font-normal text-slate-400 capitalize">
                      {user.role === "admin" ? "Administrator" : user.role === "staff" ? "Staff" : user.status}
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {isOps ? (
                    <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="nav-admin">
                      <LayoutDashboard className="h-4 w-4 mr-2" /> Admin Portal
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem onClick={() => navigate("/account")} data-testid="nav-account">
                      <LayoutDashboard className="h-4 w-4 mr-2" /> My Account
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => { logout(); navigate("/"); }} data-testid="logout-btn">
                    <LogOut className="h-4 w-4 mr-2" /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2">
                <Button variant="ghost" onClick={() => navigate("/login")} data-testid="nav-login" className="rounded-full font-semibold px-3 sm:px-4">
                  Sign in
                </Button>
                <Button onClick={() => navigate("/register")} data-testid="nav-register" className="bg-[#FFDA00] hover:bg-[#f2ce00] text-slate-900 font-semibold rounded-full">
                  Register
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
